import { useLayoutEffect, type RefObject } from 'react'

import { canCrossfade } from '@/lib/motion/motion-preference'
import { SHIMMER_CYCLE_MS, SKELETON_DELAY_MS } from '@/lib/motion/skeleton'

/**
 * How long a navigation may run before the router shows the destination's
 * pending page: one frame, so the new page's frame — its title row and rail
 * — is on screen with the next paint after the click. Its skeleton blocks
 * still wait 80ms before they paint (`--motion-skeleton-delay`), which is
 * what keeps a fast load from flashing grey; a longer router delay would
 * keep the OLD page up instead.
 *
 * One frame rather than zero, measured (2026-10-05, router-core 1.171): at
 * zero the router mounts the pending page even for a load served from the
 * cache, and replaces it a millisecond later — a whole pending render, rail
 * and all, plus its scroll reset, on every click. A cache hit settles in
 * microtasks, well inside one frame, so at 16 it never draws the pending
 * page at all; a 200ms load still shows it at ~20ms.
 *
 * Here rather than in app/router.tsx so a test can build its router with the
 * app's real timing: router.tsx imports the generated route tree.
 */
export const PAGE_PENDING_MS = 16

/**
 * No minimum hold either. The router counts it from the moment the pending
 * page mounts, not from when its skeleton paints, so any hold would delay a
 * load that lands inside the 80ms wait — the very loads the wait is for —
 * and a hover preload that is still in flight at the click is exactly that.
 */
export const PAGE_PENDING_MIN_MS = 0

/**
 * A loaded page replacing its own pending page must look like that page
 * carrying on, not a second page arriving: if the frame entered again, the
 * title and rail would vanish and fade back in the moment the data landed.
 */
interface PendingFrame {
  pathname: string
  shownAt: number
  endedAt: number | null
  /** Its skeleton has painted: it has been up past the 80ms wait. */
  painted: boolean
  /**
   * A copy of the pending column as it stood when it made way, for the loaded
   * page to fade out over its own sections — taken only when its skeleton had
   * painted and there is motion to show it.
   */
  column: HTMLElement | null
}

/** The pending page on screen now, or the one that just made way. */
let pendingFrame: PendingFrame | null = null

/**
 * A pending page unmounts in the same commit that mounts its replacement, and
 * React runs the unmount's cleanup first. Anything older is a pending page
 * that went away on its own (a redirect, a navigation elsewhere), and the
 * page mounting now enters fresh.
 */
const HANDOFF_WINDOW_MS = 100

/**
 * How long the pending column's copy stays over the loaded one: its last row
 * starts leaving on the stagger's last step (`--motion-stagger-cap`, 320) and
 * takes `--motion-skeleton-out` (160) to go.
 */
const HANDOFF_OUT_MS = 320 + 160

/** `animate-skeleton-in`'s fade, after its wait: `--motion-med`. */
const SKELETON_APPEAR_MS = 220

/**
 * How far a pending page's blocks had faded in `elapsed` ms after it mounted:
 * `animate-skeleton-in` (80ms wait, then 220ms on `--ease-in-out`, whose
 * bezier is fitted to the in-out cubic used here). The copy starts at this
 * opacity, so a load that lands mid-fade does not flash the blocks to full
 * strength before they leave.
 */
const skeletonOpacityAt = (elapsed: number): number => {
  const progress = Math.min(1, Math.max(0, (elapsed - SKELETON_DELAY_MS) / SKELETON_APPEAR_MS))
  return progress < 0.5 ? 4 * progress ** 3 : 1 - (-2 * progress + 2) ** 3 / 2
}

/**
 * Whether the page rendering now will take over a pending page whose skeleton
 * the member saw, so its column can be marked `data-handoff` (the shorter
 * rise, globals.css) in the render that creates it. Read once, as the loaded
 * page first renders, while its pending page is still mounted. Marking the
 * column after the commit instead restyled and re-laid the whole column in
 * the reveal frame: 15ms more of it at 4x CPU, measured.
 */
export function isHandingOff(): boolean {
  return pendingFrame?.painted === true && pendingFrame.pathname === window.location.pathname
}

/** The copy on screen now, and the loaded column it stands over. */
let handoffCopy: { copy: HTMLElement; column: HTMLElement } | null = null

const removeCopy = (copy: HTMLElement): void => {
  copy.remove()
  if (handoffCopy?.copy === copy) handoffCopy = null
}

/**
 * Lays `copy`, the pending column as it made way, over the loaded `column`
 * and fades each of its rows out on the step its section fades up on, so no
 * part of the page goes blank between the skeleton and the content (the
 * `page-handoff` rules in globals.css). It is an inert, hidden picture: no
 * id, no page-region or pending marks a query could mistake for the page,
 * no entrance of its own to play again.
 *
 * It goes on `<body>`, placed over the column by measurement, not into the
 * column: a child added to the column restyled every section in it (the
 * stagger picks sections by position), in the commit that reveals them — 26ms
 * of that commit at 4x CPU, measured. It is placed in the next animation
 * frame, ahead of that frame's paint, so the measuring reads the layout the
 * frame needs anyway rather than forcing the commit's own (17ms more of it).
 * It removes itself once its last row has gone, or sooner with its page
 * (`dropOrphanCopy`).
 */
function handOff(column: HTMLElement, copy: HTMLElement, shownAt: number): void {
  for (const attribute of ['id', 'data-page-region', 'data-page-pending', 'aria-busy']) {
    copy.removeAttribute(attribute)
  }
  copy.querySelectorAll('[id]').forEach((element) => {
    element.removeAttribute('id')
  })
  copy.querySelectorAll('.animate-skeleton-in').forEach((element) => {
    element.classList.remove('animate-skeleton-in')
  })
  copy.setAttribute('aria-hidden', 'true')
  copy.inert = true
  copy.classList.add('page-handoff')
  Array.from(copy.children).forEach((row, index) => {
    if (row instanceof HTMLElement) row.style.setProperty('--i', String(index))
  })

  requestAnimationFrame((now) => {
    if (!column.isConnected) return
    const opacity = skeletonOpacityAt(now - shownAt)
    if (opacity < 1) copy.style.opacity = String(opacity)
    // The highlight holds where the shared clock has it now (syncShimmer),
    // the spot the originals reached, rather than sweeping on: a paused
    // animation needs no layer, and a fresh one per block, styled in the
    // reveal frame, made that frame longer for a sweep nobody follows while
    // it fades.
    copy.style.setProperty('--shimmer-at', `${String(-Math.round(now % SHIMMER_CYCLE_MS))}ms`)
    const box = column.getBoundingClientRect()
    copy.style.top = `${String(box.top + window.scrollY)}px`
    copy.style.left = `${String(box.left + window.scrollX)}px`
    copy.style.width = `${String(box.width)}px`
    document.body.append(copy)
    handoffCopy = { copy, column }
    window.setTimeout(() => {
      removeCopy(copy)
    }, HANDOFF_OUT_MS)
  })
}

/**
 * Takes the copy down early when the page it stood over has gone (a click
 * away inside its 480ms). Checked after the commit, when an unmounted column
 * has left the document: a layout effect's cleanup runs before React removes
 * the DOM, and StrictMode's rehearsal unmount, in development, removes none.
 */
const dropOrphanCopy = (): void => {
  queueMicrotask(() => {
    if (handoffCopy !== null && !handoffCopy.column.isConnected) removeCopy(handoffCopy.copy)
  })
}

/**
 * Hands a page's entrance from its pending page to its loaded page.
 *
 * Mounted pending, it records when its frame began to enter, and as it makes
 * way it copies its column (`columnRef`) if the member saw its skeleton.
 * Mounted loaded, it takes the record over — when it is this pathname's, and
 * from a pending page that made way in this very commit — and sets
 * `--page-elapsed` on `ref` to how long that frame has been up. globals.css
 * subtracts it from every frame animation's delay, so the root fade, the
 * title row and the rail start that far in and carry on where the pending
 * page left them. The copy goes over the loaded column and fades out as the
 * sections arrive (`handOff`).
 *
 * A layout effect, so the delay and the copy are in place before the first
 * paint; a CSS animation that has started already (a layout read can force
 * one) takes a new delay as if it had been there from the start. The pending
 * page's cleanup runs while its DOM is still attached, before React removes
 * it in the same commit.
 */
export function usePageFrame(
  ref: RefObject<HTMLElement | null>,
  columnRef: RefObject<HTMLElement | null>,
  pending: boolean,
): void {
  useLayoutEffect(() => {
    const now = performance.now()

    if (pending) {
      const frame: PendingFrame = {
        pathname: window.location.pathname,
        shownAt: now,
        endedAt: null,
        painted: false,
        column: null,
      }
      pendingFrame = frame
      // Asked now, while nothing else is changing, so the listener is set up
      // before the handoff needs the answer.
      canCrossfade()
      const painting = window.setTimeout(() => {
        frame.painted = true
      }, SKELETON_DELAY_MS)
      // The column this pending page drew, still attached when the cleanup runs.
      const column = columnRef.current
      return () => {
        window.clearTimeout(painting)
        frame.endedAt = performance.now()
        if (column !== null && frame.painted && canCrossfade()) {
          frame.column = column.cloneNode(true) as HTMLElement
        }
      }
    }

    const frame = pendingFrame
    pendingFrame = null
    if (frame === null || frame.pathname !== window.location.pathname) return dropOrphanCopy
    if (frame.endedAt === null || now - frame.endedAt > HANDOFF_WINDOW_MS) return dropOrphanCopy
    ref.current?.style.setProperty('--page-elapsed', `${String(Math.round(now - frame.shownAt))}ms`)
    const column = columnRef.current
    if (frame.column !== null && column !== null) handOff(column, frame.column, frame.shownAt)
    return dropOrphanCopy
    // Mount only: a ScreenLayout never flips `pending` once mounted — a route's
    // pending page and its loaded page are two different instances.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above
  }, [])
}
